'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from './utils/cn';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const portalVariants = cva('fixed inset-0 z-50 flex', {
  defaultVariants: { position: 'right' },
  variants: {
    position: {
      bottom: 'items-end',
      left: 'justify-start',
      right: 'justify-end',
      top: 'items-start',
    },
  },
});

interface SheetPortalProps extends SheetPrimitive.DialogPortalProps, VariantProps<typeof portalVariants> {}

function SheetPortal({ position, children, ...props }: SheetPortalProps) {
  return (
    <SheetPrimitive.Portal {...props}>
      <div className={portalVariants({ position })}>{children}</div>
    </SheetPrimitive.Portal>
  );
}
SheetPortal.displayName = SheetPrimitive.Portal.displayName;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'data-[state=closed]:fade-out data-[state=open]:fade-in fixed inset-0 z-50 bg-background/80 backdrop-blur-xs transition-all duration-100 data-[state=closed]:animate-out',
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva('fixed z-50 scale-100 gap-4 border bg-background p-6 opacity-100 shadow-lg', {
  compoundVariants: [
    {
      class: 'max-h-screen',
      position: ['top', 'bottom'],
      size: 'content',
    },
    {
      class: 'h-1/3',
      position: ['top', 'bottom'],
      size: 'default',
    },
    {
      class: 'h-1/4',
      position: ['top', 'bottom'],
      size: 'sm',
    },
    {
      class: 'h-1/2',
      position: ['top', 'bottom'],
      size: 'lg',
    },
    {
      class: 'h-5/6',
      position: ['top', 'bottom'],
      size: 'xl',
    },
    {
      class: 'h-screen',
      position: ['top', 'bottom'],
      size: 'full',
    },
    {
      class: 'w-full lg:max-w-screen',
      position: ['right', 'left'],
      size: 'content',
    },
    {
      class: 'w-full lg:w-1/3',
      position: ['right', 'left'],
      size: 'default',
    },
    {
      class: 'w-full lg:w-1/4',
      position: ['right', 'left'],
      size: 'sm',
    },
    {
      class: 'w-full lg:w-1/2',
      position: ['right', 'left'],
      size: 'lg',
    },
    {
      class: 'w-full lg:w-5/6',
      position: ['right', 'left'],
      size: 'xl',
    },
    {
      class: 'w-screen',
      position: ['right', 'left'],
      size: 'full',
    },
  ],
  defaultVariants: {
    position: 'right',
    size: 'default',
  },
  variants: {
    position: {
      bottom: 'slide-in-from-bottom w-full animate-in duration-300',
      left: 'slide-in-from-left h-full animate-in duration-300',
      right: 'slide-in-from-right h-full animate-in duration-300',
      top: 'slide-in-from-top w-full animate-in duration-300',
    },
    size: {
      content: '',
      default: '',
      full: '',
      lg: '',
      sm: '',
      xl: '',
    },
  },
});

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  onClose?: () => void;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, DialogContentProps>(
  ({ position, size, className, children, ...props }, ref) => (
    <SheetPortal position={position}>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn('overflow-y-auto', sheetVariants({ position, size }), className)}
        ref={ref}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          className='absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary'
          onClick={() => (props.onClose ? props.onClose() : null)}
        >
          <X className='h-6 w-6' />
          <span className='sr-only'>Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col space-y-2 pr-8 text-center sm:text-left', className)} {...props} />;
}
SheetHeader.displayName = 'SheetHeader';

function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col-reverse sm:flex-row sm:space-x-2', className)} {...props} />;
}
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title className={cn('font-semibold text-2xl text-foreground', className)} ref={ref} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description className={cn('text-muted-foreground text-sm', className)} ref={ref} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

const SheetClose = SheetPrimitive.Close;

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
